import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import React, { useContext } from 'react';
import LinearProgressWithLabel from '@mui/material/LinearProgress';
import { WarningAmberOutlined } from '@mui/icons-material';
import { useTranslation } from "react-i18next";
import { Chip, Skeleton, useTheme } from "@mui/material";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ChartBanner from './ChartBanner';
import { formatValue, normalize, percentageFormat } from '../../../../helpers/helpers';
import { UserContext } from '../../../../context/UserContext';
import { TableChip } from '../../../../components/EnhancedTableToolbar';

export interface MultipleProgressChartData {
    key: string;
    value: number;
}

export interface MultipleProgressChartProps {
    title: string;
    currency?: string;
    showCurrency?: boolean;
    description?: string;
    textLink?: string;
    linkChart?: string;
    isLoading: boolean;
    isError: boolean;
    data?: MultipleProgressChartData[];
    sum?: number;
    chips?: TableChip[];
}

interface BarColor {
    front: string;
    back: string;
}

const MultipleProgressChart = ({
    title,
    currency,
    showCurrency = true,
    description,
    textLink,
    linkChart,
    isLoading,
    isError,
    data,
    sum,
    chips,
}: MultipleProgressChartProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { locale } = useContext(UserContext);

    const barColors: BarColor[] = [
        {
            front: theme.palette.blue.main,
            back: theme.palette.blue.light
        },
        {
            front: theme.palette.cyan.main,
            back: theme.palette.cyan.light
        },
        {
            front: theme.palette.teal.main,
            back: theme.palette.teal.light
        },
        {
            front: theme.palette.lightGreen.main,
            back: theme.palette.lightGreen.light
        },
        {
            front: theme.palette.lime.main,
            back: theme.palette.lime.light
        },
        {
            front: theme.palette.yellow.main,
            back: theme.palette.yellow.light
        },
        {
            front: theme.palette.orange.main,
            back: theme.palette.orange.light
        },
        {
            front: theme.palette.deepOrange.main,
            back: theme.palette.deepOrange.light
        },
        {
            front: theme.palette.fucsia.main,
            back: theme.palette.fucsia.light
        },
        {
            front: theme.palette.purple.main,
            back: theme.palette.purple.light
        }
    ];

    const getFrontColor = (colorIndex: number) => barColors[colorIndex % barColors.length].front;
    const getBackColor = (colorIndex: number) => barColors[colorIndex % barColors.length].back;
    const showNoDataWarningBanner = !data?.length ||
        data.every(dataObject => !dataObject?.value);

    return (
        <>
            {!isLoading && (
                <Box>
                    <Card elevation={0} sx={{ border: `solid 1px`, borderColor: theme.palette.grey['12p'], marginX: '8px', padding: '12px', flex: 1, height: {}, display: 'flex', flexDirection: 'column', breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                        {isError &&
                            <Box sx={{ marginRight: '4px', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <ChartBanner type='error' title={t("screens.dashboarding.dataError")} />
                            </Box>
                        }
                        <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', margin: '6px', marginTop: '15px', flex: 1 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flex: 1,
                                        width: '100%',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            width: '100%',
                                        }}
                                    >
                                        <Typography variant="h2">
                                            {title}
                                        </Typography>

                                        {!isError && showNoDataWarningBanner && (
                                            <Chip
                                                variant="filled"
                                                size="small"
                                                label={t("screens.dashboarding.dataUnavailable")}
                                                icon={<WarningAmberOutlined />}
                                                sx={{
                                                    backgroundColor: theme.palette.warning['190p'],
                                                    marginRight: '16px',
                                                    '& .MuiChip-label': {
                                                        color: theme.palette.warning['160p'],
                                                    },
                                                }}
                                            />
                                        )}
                                    </Box>
                                    <Typography sx={{ fontSize: '14px', fontWeight: '400', marginTop: '10px' }}>{description}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, marginTop: 1 }}>
                                    {chips?.map((chip) => (
                                        <Chip
                                            key={`chip-${chip.label}`}
                                            variant="filled"
                                            size='small'
                                            label={chip.label}
                                            sx={{
                                                marginRight: '8px',
                                                backgroundColor:
                                                    chip.labelType === 'periodCompare'
                                                        ? theme.palette.amber.light
                                                        : theme.palette.info['190p'],
                                                '& .MuiChip-label': {
                                                    color: chip.labelType === 'periodCompare' ? theme.palette.warning['160p'] : theme.palette.info['160p']
                                                }
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{ maxHeight: '200px', overflow: 'auto', marginTop: '50px' }}>
                            {data && data.map((dataObject, index) =>
                                <React.Fragment key={dataObject.key}>
                                    <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto', justifyContent: 'space-between' }}>
                                        <Typography variant="h3" >{dataObject.key}</Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto', marginRight: '5px' }}>
                                            <Typography sx={{ fontSize: '16px', fontWeight: '600', marginRight: '20px' }}>{formatValue(dataObject.value, showCurrency, currency, locale)}</Typography>
                                            <Typography sx={{ fontSize: '16px', fontWeight: '400' }}>{sum ? percentageFormat(dataObject.value / sum, locale) : percentageFormat(sum)}</Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', margin: '6px', marginTop: '15px', marginBottom: '20px' }}>
                                        <Box sx={{ width: '100%' }}>
                                            <LinearProgressWithLabel variant="determinate" value={sum ? normalize(dataObject.value / sum) : 0}
                                                sx={{
                                                    '& .MuiLinearProgress-bar': {
                                                        backgroundColor: getFrontColor(index),
                                                        borderRadius: 5
                                                    },
                                                    backgroundColor: getBackColor(index),

                                                }}

                                            />
                                        </Box>
                                    </Box>
                                </React.Fragment>
                            )}
                        </Box>
                        {!!linkChart && <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'auto' }}>
                            <Button href={linkChart} sx={{ fontSize: '16px', fontWeight: '600', color: 'primary.main' }}>{textLink}</Button>
                        </Box>}
                        {!linkChart && <Box sx={{ marginTop: '50px' }} />}
                    </Card>
                </Box>
            )}
            {isLoading &&
                <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', marginTop: '16px' }}>
                    <Card elevation={0} sx={{ border: 'solid 1px', borderColor: theme.palette.grey['12p'], marginX: '8px', padding: '12px', flex: 1 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto', justifyContent: 'space-between', margin: '6px' }}>
                            <Skeleton variant="text" width={120} height={50} />
                            <Skeleton variant="text" width={120} height={50} />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto', marginTop: '40px', justifyContent: 'space-between' }}>
                            <Skeleton variant="text" width="40%" height={16} />
                            <Skeleton variant="text" width={30} height={16} />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', margin: '6px', marginTop: '15px' }}>
                            <Box sx={{ width: '90%', marginTop: '12px' }}>
                                <Skeleton sx={{ height: '8px', backgroundColor: '#f0f0f0' }} />
                            </Box>
                            <Skeleton variant="text" width={30} height={16} />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto', marginTop: '40px', justifyContent: 'space-between' }}>
                            <Skeleton variant="text" width="40%" height={16} />
                            <Skeleton variant="text" width={30} height={16} />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', margin: '6px', marginTop: '15px' }}>
                            <Box sx={{ width: '90%', marginTop: '12px' }}>
                                <Skeleton sx={{ height: '8px', backgroundColor: '#f0f0f0' }} />
                            </Box>
                            <Skeleton variant="text" width={30} height={16} />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto', marginTop: '40px', justifyContent: 'space-between' }}>
                            <Skeleton variant="text" width="40%" height={16} />
                            <Skeleton variant="text" width={30} height={16} />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', margin: '6px', marginTop: '15px' }}>
                            <Box sx={{ width: '90%', marginTop: '12px' }}>
                                <Skeleton sx={{ height: '8px', backgroundColor: '#f0f0f0' }} />
                            </Box>
                            <Skeleton variant="text" width={30} height={16} />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto', marginTop: '40px', justifyContent: 'space-between' }}>
                            <Skeleton variant="text" width="40%" height={16} />
                            <Skeleton variant="text" width={30} height={16} />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', margin: '6px', marginTop: '15px' }}>
                            <Box sx={{ width: '90%', marginTop: '12px' }}>
                                <Skeleton sx={{ height: '8px', backgroundColor: '#f0f0f0' }} />
                            </Box>
                            <Skeleton variant="text" width={30} height={16} />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '15px', marginTop: '15px' }}>
                            <Skeleton variant="rectangular" width={120} height={36} />
                        </Box>
                    </Card>
                </Box>
            }
        </>
    );
}

export default MultipleProgressChart;
